import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGetAnswerCodes } from 'api/quote-marketplace';
import type {
  AnswerCodesData,
  AnswerCodeOption,
  HabConditionalRule,
} from 'api/quote-marketplace';
import { useEffectiveDate } from './EffectiveDateContext';
import { useProductTarget, type ProductTarget } from './ProductTargetContext';
import { isEffectiveDateWarningCode } from '../constants';

const LABEL_OVERRIDES: Record<string, Record<string, string>> = {
  property_type: {
    'Basement apartment': 'Detached dwelling with basement apartment',
  },
};

export type AnswerCodeField = keyof Omit<
  AnswerCodesData,
  'synced_at' | 'hab_conditional_rules' | 'hab_conditional_field_names'
>;

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectOptionWithTypes extends SelectOption {
  available_for_types?: string[];
}

export interface LicenseDateField {
  field: string;
  label: string;
  acturis_name: string;
  allowed_class_codes: string[];
}

export interface MotorcycleConfig {
  class_question: string;
  classes: SelectOption[];
  date_fields: LicenseDateField[];
}

export interface LicenseConfig {
  class_question: string;
  classes: SelectOption[];
  date_fields: LicenseDateField[];
  motorcycle: MotorcycleConfig;
  commercial_classes: SelectOption[];
}

export interface EffectiveDateRange {
  minDate: Date;
  maxDate: Date;
}

export interface AnswerCodesWarning {
  code: string;
  message: string;
  requestedDate: string;
  validFrom: string | null;
  validTo: string | null;
}

export type HeatingSource = 'primary' | 'auxiliary';

interface UseAnswerCodesReturn {
  answerCodes: AnswerCodesData | undefined;
  isLoading: boolean;
  error: Error | null;
  warning: AnswerCodesWarning | null;
  isEffectiveDateValid: boolean;
  getOptions: (field: AnswerCodeField) => SelectOption[];
  getOptionsWithTypes: (field: AnswerCodeField) => SelectOptionWithTypes[];
  getLabel: (value: string, field: AnswerCodeField) => string;
  getCode: (value: string, field: AnswerCodeField) => string;
  getYesNoCode: (value: 'Yes' | 'No') => string;
  getSpeedingConvictionCodes: () => string[];
  getConvictionOtherCodes: () => string[];
  getSuspensionOtherCodes: () => string[];
  getIncomeReplacementOtherCodes: () => string[];
  getOccupationOtherCodes: () => string[];
  getHabOccupationOtherCodes: () => string[];
  getLapseOtherCodes: (isApplicant: boolean) => string[];
  getCancelDeclineOtherCodes: (isApplicant: boolean) => string[];
  getApplicantRelationCode: () => string;
  getAutomobileClaimTypeCode: () => string;
  getFraudPolicyStatusOptions: (isApplicant: boolean) => SelectOption[];
  getLicenseConfig: (province: string) => LicenseConfig | null;
  effectiveDateRange: EffectiveDateRange | null;
  isOtherDeductible: (code: string, field: AnswerCodeField) => boolean;
  isHeatingTypeNone: (code: string) => boolean;
  shouldShowConditionalField: (
    field: string,
    triggerOrEntity: string | Record<string, unknown>,
  ) => boolean;
  shouldShowConditionalFieldForHeatingSource: (
    field: string,
    location: Record<string, unknown>,
    heatingSource: HeatingSource,
  ) => boolean;
  getHabRule: (field: string) => HabConditionalRule | undefined;
  getCodeByLabel: (label: string, field: AnswerCodeField) => string;
  getValidFuelOptions: (heatingTypeCode: string) => SelectOption[];
}

export const useAnswerCodes = (
  productTarget?: ProductTarget,
): UseAnswerCodesReturn => {
  const contextProductTarget = useProductTarget();
  const effectiveDate = useEffectiveDate();
  const target = productTarget ?? contextProductTarget;

  const { data, isLoading, error } = useQuery({
    queryKey: ['acturis', 'answer-codes', effectiveDate, target],
    queryFn: () => apiGetAnswerCodes(effectiveDate, target),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
  });

  const getOptions = useCallback(
    (field: AnswerCodeField): SelectOption[] => {
      if (!data) return [];
      const options = data[field];
      if (!Array.isArray(options)) return [];
      const overrides = LABEL_OVERRIDES[field];
      return [
        { value: '', label: 'Select...' },
        ...options.map((o: AnswerCodeOption) => ({
          value: o.code,
          label: overrides?.[o.label] ?? o.label,
        })),
      ];
    },
    [data],
  );

  const getOptionsWithTypes = useCallback(
    (field: AnswerCodeField): SelectOptionWithTypes[] => {
      if (!data) return [];
      const options = data[field];
      if (!Array.isArray(options)) return [];
      const overrides = LABEL_OVERRIDES[field];
      return [
        { value: '', label: 'Select...' },
        ...options.map((o: AnswerCodeOption) => ({
          value: o.code,
          label: overrides?.[o.label] ?? o.label,
          available_for_types: o.available_for_types,
        })),
      ];
    },
    [data],
  );

  const findOption = useCallback(
    (value: string, field: AnswerCodeField): AnswerCodeOption | undefined => {
      if (!data || !value) return undefined;
      const options = data[field];
      if (!Array.isArray(options)) return undefined;

      let option = options.find((o: AnswerCodeOption) => o.code === value);
      if (option) return option;

      const valueLower = value.toLowerCase();
      option = options.find(
        (o: AnswerCodeOption) => o.label.toLowerCase() === valueLower,
      );
      return option;
    },
    [data],
  );

  const getLabel = useCallback(
    (value: string, field: AnswerCodeField): string => {
      const option = findOption(value, field);
      return option?.label ?? value;
    },
    [findOption],
  );

  const getCode = useCallback(
    (value: string, field: AnswerCodeField): string => {
      const option = findOption(value, field);
      return option?.code ?? value;
    },
    [findOption],
  );

  const getYesNoCode = useCallback(
    (value: 'Yes' | 'No'): string => {
      const option = findOption(
        value,
        'any_policy_cancelled_declined_or_renewal_rejected_by_any_insurer',
      );
      return option?.code ?? '';
    },
    [findOption],
  );

  const getSpeedingConvictionCodes = useCallback((): string[] => {
    return data?.conditional_rules?.speed_over_limit_kmh?.show_for_codes ?? [];
  }, [data]);

  const getConvictionOtherCodes = useCallback((): string[] => {
    return data?.conditional_rules?.conviction_code_other?.show_for_codes ?? [];
  }, [data]);

  const getSuspensionOtherCodes = useCallback((): string[] => {
    return (
      data?.conditional_rules?.suspension_reason_other?.show_for_codes ?? []
    );
  }, [data]);

  const getIncomeReplacementOtherCodes = useCallback((): string[] => {
    return (
      data?.conditional_rules?.income_replacement_limit_per_week_cad_other
        ?.show_for_codes ?? []
    );
  }, [data]);

  const getOccupationOtherCodes = useCallback((): string[] => {
    return data?.conditional_rules?.occupation_other?.show_for_codes ?? [];
  }, [data]);

  const getHabOccupationOtherCodes = useCallback((): string[] => {
    return data?.hab_occupation_other?.show_for_codes ?? [];
  }, [data]);

  const getLapseOtherCodes = useCallback(
    (isApplicant: boolean): string[] => {
      const rule = isApplicant
        ? data?.conditional_rules?.lapse_reason_other_applicant
        : data?.conditional_rules?.lapse_reason_other_non_applicant;
      return rule?.show_for_codes ?? [];
    },
    [data],
  );

  const getCancelDeclineOtherCodes = useCallback(
    (isApplicant: boolean): string[] => {
      const rule = isApplicant
        ? data?.conditional_rules?.cancel_decline_reason_other_applicant
        : data?.conditional_rules?.cancel_decline_reason_other_non_applicant;
      return rule?.show_for_codes ?? [];
    },
    [data],
  );

  const getApplicantRelationCode = useCallback((): string => {
    return data?.applicant_relation_code ?? '';
  }, [data]);

  const getAutomobileClaimTypeCode = useCallback((): string => {
    if (!data?.claim_type) return '';
    const autoOption = data.claim_type.find(
      (o: AnswerCodeOption) => o.label.toLowerCase() === 'automobile',
    );
    return autoOption?.code ?? '';
  }, [data]);

  const getFraudPolicyStatusOptions = useCallback(
    (isApplicant: boolean): SelectOption[] => {
      const options = isApplicant
        ? data?.fraud_policy_status_applicant
        : data?.fraud_policy_status_non_applicant;
      if (!options) return [];
      return [
        { value: '', label: 'Select...' },
        ...options.map((o) => ({ value: o.code, label: o.label })),
      ];
    },
    [data],
  );

  const isOtherDeductible = useCallback(
    (code: string, field: AnswerCodeField): boolean => {
      if (!code) return false;
      const label = getLabel(code, field);
      return label.toLowerCase() === 'other';
    },
    [getLabel],
  );

  const isHeatingTypeNone = useCallback(
    (code: string): boolean => {
      if (!code) return true;
      const label = getLabel(code, 'primary_heating_type_installed');
      return label.toLowerCase() === 'none';
    },
    [getLabel],
  );

  const getLicenseConfig = useCallback(
    (province: string): LicenseConfig | null => {
      if (!data?.license_config) return null;
      const provinceCode = province.toUpperCase();
      const config = data.license_config[provinceCode];
      if (!config) return null;

      return {
        class_question: config.class_question,
        classes: [
          { value: '', label: 'Select...' },
          ...config.classes.map((c) => ({ value: c.code, label: c.label })),
        ],
        date_fields: config.date_fields,
        motorcycle: {
          class_question: config.motorcycle.class_question,
          classes: [
            { value: '', label: 'Select...' },
            ...config.motorcycle.classes.map((c) => ({
              value: c.code,
              label: c.label,
            })),
          ],
          date_fields: config.motorcycle.date_fields,
        },
        commercial_classes: [
          { value: '', label: 'Select...' },
          ...config.commercial_classes.map((c) => ({
            value: c.code,
            label: c.label,
          })),
        ],
      };
    },
    [data],
  );

  const effectiveDateRange = useMemo((): EffectiveDateRange | null => {
    const range = data?.effective_date_range;
    if (!range?.min_date || !range?.max_date) return null;
    return {
      minDate: new Date(range.min_date + 'T00:00:00'),
      maxDate: new Date(range.max_date + 'T00:00:00'),
    };
  }, [data]);

  const warning = useMemo((): AnswerCodesWarning | null => {
    if (!data?.warning) return null;
    return {
      code: data.warning.code,
      message: data.warning.message,
      requestedDate: data.warning.requested_date,
      validFrom: data.warning.valid_from,
      validTo: data.warning.valid_to ?? null,
    };
  }, [data]);

  const isEffectiveDateValid = useMemo(
    () => !isLoading && !isEffectiveDateWarningCode(data?.warning?.code),
    [isLoading, data?.warning?.code],
  );

  const shouldShowConditionalField = useCallback(
    (
      field: string,
      triggerOrEntity: string | Record<string, unknown>,
    ): boolean => {
      if (
        data?.hab_conditional_field_names &&
        !data.hab_conditional_field_names.includes(field)
      ) {
        console.warn(`[useAnswerCodes] Unknown conditional field: ${field}`);
      }

      const rule = data?.hab_conditional_rules?.[field];
      if (!rule?.rules) return true;

      if (!triggerOrEntity) return false;

      if (typeof triggerOrEntity === 'string') {
        return rule.rules.some((r) =>
          r.show_for_codes.includes(triggerOrEntity),
        );
      }

      const entity = triggerOrEntity;
      return rule.rules.some((r) => {
        const triggerValue = entity[r.trigger_field];
        if (!triggerValue) return false;
        return r.show_for_codes.includes(String(triggerValue));
      });
    },
    [data],
  );

  const shouldShowConditionalFieldForHeatingSource = useCallback(
    (
      field: string,
      location: Record<string, unknown>,
      heatingSource: HeatingSource,
    ): boolean => {
      const rule = data?.hab_conditional_rules?.[field];
      if (!rule?.rules) return false;

      const prefix =
        heatingSource === 'primary' ? 'primary_heating' : 'auxiliary_heating';

      return rule.rules.some((r) => {
        if (!r.trigger_field.startsWith(prefix)) return false;
        const triggerValue = location[r.trigger_field];
        if (!triggerValue) return false;
        return r.show_for_codes.includes(String(triggerValue));
      });
    },
    [data],
  );

  const getHabRule = useCallback(
    (field: string): HabConditionalRule | undefined =>
      data?.hab_conditional_rules?.[field],
    [data],
  );

  const getCodeByLabel = useCallback(
    (label: string, field: AnswerCodeField): string => {
      if (!data || !label) return '';
      const options = data[field];
      if (!Array.isArray(options)) return '';
      const labelLower = label.toLowerCase();
      const option = options.find(
        (o: AnswerCodeOption) => o.label.toLowerCase() === labelLower,
      );
      return option?.code ?? '';
    },
    [data],
  );

  const getValidFuelOptions = useCallback(
    (heatingTypeCode: string): SelectOption[] => {
      if (!data) return [];

      const allFuelOptions = getOptions('primary_heating_fuel_type');
      if (!heatingTypeCode) return allFuelOptions;

      const mapping = data.heating_fuel_mapping;
      if (!mapping || !mapping[heatingTypeCode]) {
        return allFuelOptions;
      }

      const validCodes = mapping[heatingTypeCode];
      return allFuelOptions.filter(
        (opt) => opt.value === '' || validCodes.includes(opt.value),
      );
    },
    [data, getOptions],
  );

  return {
    answerCodes: data,
    isLoading,
    error,
    warning,
    isEffectiveDateValid,
    getOptions,
    getOptionsWithTypes,
    getLabel,
    getCode,
    getYesNoCode,
    getSpeedingConvictionCodes,
    getConvictionOtherCodes,
    getSuspensionOtherCodes,
    getIncomeReplacementOtherCodes,
    getOccupationOtherCodes,
    getHabOccupationOtherCodes,
    getLapseOtherCodes,
    getCancelDeclineOtherCodes,
    getApplicantRelationCode,
    getAutomobileClaimTypeCode,
    getFraudPolicyStatusOptions,
    getLicenseConfig,
    effectiveDateRange,
    isOtherDeductible,
    isHeatingTypeNone,
    shouldShowConditionalField,
    shouldShowConditionalFieldForHeatingSource,
    getHabRule,
    getCodeByLabel,
    getValidFuelOptions,
  };
};
